/**
 * Product Selection Manager Class
 * Handles the product selection functionality for Buy 1-4 cards
 * Manages selected products array and card updates
 */
class ProductSelectionManager {
  constructor() {
    this.selectedProducts = [];
    this.maxProducts = 4;
    this.replacementCardIndex = null;
    this.init();
  }



  /**
   * Initialize the product selection manager
   */
  init() {
    this.bindEvents();
    this.updateCards();
    this.updatePricingDisplay();
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    this.bindCardEvents();
    this.bindModalEvents();
    this.bindAddToCartEvents();
    this.bindQuantityEvents();
    this.bindSubscriptionEvents();
  }

  /**
   * Bind card-related event listeners
   */
  bindCardEvents() {
    // Use event delegation for quantity option card clicks to handle both original and duplicate slides
    document.addEventListener('click', (e) => {
      const quantityOption = e.target.closest('.quantity-option');
      if (quantityOption) {
        // Don't trigger if clicking on the clear button
        if (e.target.closest('.quantity-option__clear-btn')) {
          return;
        }
        e.preventDefault();
        
        // For original slides, use the card index + 1
        const slide = quantityOption.closest('.swiper-slide');
        if (slide && !slide.classList.contains('duplicate-slide')) {
          const allOriginalSlides = document.querySelectorAll('.swiper-slide:not(.duplicate-slide)');
          const slideIndex = Array.from(allOriginalSlides).indexOf(slide);
          if (slideIndex !== -1) {
            this.openProductSelection(slideIndex + 1);
          }
        } else if (slide && slide.classList.contains('duplicate-slide')) {
           // For duplicate slides, always open product selection modal
           this.findAndOpenAvailableSlot();
        }
      }
    }); 

    // Note: Add to cart button removed - now using quantity-based selection

    // Override variant card clicks to store selection data
    const variantCards = document.querySelectorAll('.product-variant-card');
    variantCards.forEach(card => {
      // Remove existing event listeners by cloning the element
      const newCard = card.cloneNode(true);
      card.parentNode.replaceChild(newCard, card);
      
      // Add new event listener
      newCard.addEventListener('click', (e) => {
        // Don't trigger variant selection if clicking on quantity controls or add to cart button
        if (e.target.closest('.product-variant-card__quantity') || e.target.closest('.add-to-cart-btn')) {
          return;
        }
        this.selectVariant(newCard);
      });
    });
  }

  /**
   * Bind modal-related event listeners
   */
  bindModalEvents() {
    // Handle product detail modal close events
    const detailModal = document.getElementById('productDetailModal');
    if (detailModal) {
      // Function to sync modal state back to variant cards
      const syncOnClose = () => {
        if (this.currentVariantData) {
          const modalQuantityInput = document.getElementById('modalQuantityInput');
          if (modalQuantityInput) {
            const quantity = parseInt(modalQuantityInput.value) || 0;
            this.syncQuantities(this.currentVariantData.id, quantity);
          }
        }
      };
      
      // Listen for modal close event (includes ESC key)
      detailModal.addEventListener('close', syncOnClose);
      
      // Listen for close button clicks - dispatch click event to back button
      const closeBtn = detailModal.querySelector('.product-detail-modal__close');
      const backBtn = document.getElementById('backToProducts');
      
      if (closeBtn && backBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          backBtn.click();
        });
      }
      
      // Listen for back button clicks
      if (backBtn) {
        backBtn.addEventListener('click', syncOnClose);
      }
      
      // Listen for backdrop clicks
      detailModal.addEventListener('click', (e) => {
        if (e.target === detailModal) {
          syncOnClose();
        }
      });
    }
  }

  /**
   * Bind subscription-related event listeners
   */
  bindSubscriptionEvents() {
    // Subscription events can be added here
  }

  /**
   * Bind quantity input events for both variant cards and modal
   */
  bindQuantityEvents() {
    // Handle quantity buttons in variant cards
    document.addEventListener('click', (e) => {
      const quantityBtn = e.target.closest('.quantity-btn');
      if (quantityBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        const action = quantityBtn.dataset.action;
        const quantityContainer = quantityBtn.closest('.product-variant-card__quantity, .product-detail-modal__quantity');
        const input = quantityContainer.querySelector('.quantity-input');
        
        if (input) {
          this.updateQuantity(input, action);
        }
      }
    });

    // Handle direct input changes
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        const input = e.target;
        const oldValue = parseInt(input.dataset.previousValue) || 0;
        
        // Validate the input first
        this.validateQuantityInput(input);
        
        const newValue = parseInt(input.value) || 0;
        
        // Only proceed if value actually changed
        if (oldValue !== newValue) {
          // Store the new value for next comparison
          input.dataset.previousValue = newValue;
          
          // Get variant data for this input
          let variantId, variantData;
          
          // Check if this is a variant card input
          const variantCard = input.closest('.product-variant-card');
          if (variantCard) {
            variantId = input.dataset.variantId;
            variantData = this.getVariantDataFromCard(variantCard);
          }
          
          // Check if this is the modal input
          if (input.id === 'modalQuantityInput' && this.currentVariantData) {
            variantId = this.currentVariantData.id;
            variantData = this.currentVariantData;
          }
          
          if (variantId && variantData) {
            // Update the selected products array based on quantity change
            this.updateSelectedProductsByQuantity(variantData, oldValue, newValue);
            
            // Sync quantities between inputs
            this.syncQuantities(variantId, newValue);
            
            // Update cards display
            this.updateCards();
          }
        }
      }
    });
    
    // Initialize previous values for all quantity inputs
    document.querySelectorAll('.quantity-input').forEach(input => {
      input.dataset.previousValue = input.value || '0';
    });
  }

  /**
   * Bind Add to Cart button events
   */
  bindAddToCartEvents() {
    // Handle Add to Cart button clicks in variant cards
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.add-to-cart-btn');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        
        const variantId = button.dataset.variantId;
        
        if (variantId) {
          // This is a variant card button
          this.toggleToQuantityInput(variantId);
        } else if (button.id === 'modalAddToCartBtn') {
          // This is the modal button
          this.toggleModalToQuantityInput();
        }
      }
    });
  }

  /**
   * Toggle variant card from Add to Cart button to quantity input
   * @param {string} variantId - The variant ID
   */
  toggleToQuantityInput(variantId) {
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    if (!variantCard) return;
    
    const button = variantCard.querySelector('.add-to-cart-btn');
    const quantityDiv = variantCard.querySelector('.product-variant-card__quantity');
    const quantityInput = quantityDiv ? quantityDiv.querySelector('.quantity-input') : null;
    
    if (button && quantityDiv && quantityInput) {
      // Hide button and show quantity input
      button.style.display = 'none';
      quantityDiv.style.display = 'flex';
      
      // Set quantity to 1 and trigger the update
      quantityInput.value = '1';
      const variantData = this.getVariantDataFromCard(variantCard);
      
      if (variantData) {
        this.updateSelectedProductsByQuantity(variantData, 0, 1);
        this.syncQuantities(variantId, 1);
        this.updateCards();
      }
    }
  }

  /**
   * Toggle modal from Add to Cart button to quantity input
   */
  toggleModalToQuantityInput() {
    const button = document.getElementById('modalAddToCartBtn');
    const quantityDiv = document.querySelector('.product-detail-modal__quantity');
    const quantityInput = document.getElementById('modalQuantityInput');
    
    if (button && quantityDiv && quantityInput && this.currentVariantData) {
      // Hide button and show quantity input
      button.style.display = 'none';
      quantityDiv.style.display = 'flex';
      
      // Set quantity to 1 and trigger the update
      quantityInput.value = '1';
      const variantId = this.currentVariantData.id;
      
      this.updateSelectedProductsByQuantity(this.currentVariantData, 0, 1);
      this.syncQuantities(variantId, 1);
      this.updateCards();
    }
  }

  /**
   * Update quantity based on action
   * @param {HTMLElement} input - The quantity input element
   * @param {string} action - 'increase' or 'decrease'
   */
  updateQuantity(input, action) {
    let currentValue = parseInt(input.value) || 0;
    const min = parseInt(input.min) || 0;
    const max = parseInt(input.max) || 99;
    const oldValue = currentValue;
    
    if (action === 'increase' && currentValue < max) {
      currentValue++;
    } else if (action === 'decrease' && currentValue > min) {
      currentValue--;
    }
    
    input.value = currentValue;
    
    // Update previousValue to prevent infinite loops in input event
    input.dataset.previousValue = currentValue.toString();
    
    // Get variant data for this input
    let variantId, variantData;
    
    // Check if this is a variant card input
    const variantCard = input.closest('.product-variant-card');
    if (variantCard) {
      variantId = input.dataset.variantId;
      variantData = this.getVariantDataFromCard(variantCard);
    }
    
    // Check if this is the modal input
    if (input.id === 'modalQuantityInput' && this.currentVariantData) {
      variantId = this.currentVariantData.id;
      variantData = this.currentVariantData;
    }
    
    if (variantId && variantData) {
      // Update the selected products array based on quantity change
      this.updateSelectedProductsByQuantity(variantData, oldValue, currentValue);
      
      // Sync quantities between inputs
      this.syncQuantities(variantId, currentValue);
      
      // Also directly update visibility for the current input's card/modal
      if (variantCard) {
        const button = variantCard.querySelector('.add-to-cart-btn');
        const quantityDiv = variantCard.querySelector('.product-variant-card__quantity');
        if (button && quantityDiv) {
          if (currentValue > 0) {
            button.style.display = 'none';
            quantityDiv.style.display = 'flex';
          } else {
            button.style.display = 'flex';
            quantityDiv.style.display = 'none';
          }
        }
      }
      
      if (input.id === 'modalQuantityInput') {
        const modalButton = document.getElementById('modalAddToCartBtn');
        const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
        if (modalButton && modalQuantityDiv) {
          if (currentValue > 0) {
            modalButton.style.display = 'none';
            modalQuantityDiv.style.display = 'flex';
          } else {
            modalButton.style.display = 'flex';
            modalQuantityDiv.style.display = 'none';
          }
        }
      }
      
      // Update cards display
      this.updateCards();
    }
  }

  /**
   * Validate quantity input value
   * @param {HTMLElement} input - The quantity input element
   */
  validateQuantityInput(input) {
    let value = parseInt(input.value);
    const min = parseInt(input.min) || 0;
    const max = parseInt(input.max) || 99;
    
    if (isNaN(value) || value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }
    
    input.value = value;
  }

  /**
   * Get variant data from a variant card element
   * @param {HTMLElement} variantCard - The variant card element
   * @returns {Object} - The variant data object
   */
  getVariantDataFromCard(variantCard) {
    const descriptionElement = variantCard.querySelector('.product-variant-card__description');
    return {
      id: variantCard.dataset.variantId,
      title: variantCard.dataset.variantTitle,
      image: variantCard.dataset.variantImage,
      description: descriptionElement ? descriptionElement.textContent : 'Product description not available.',
      available: variantCard.dataset.variantAvailable === 'true',
      inventory: parseInt(variantCard.dataset.variantInventory) || 0,
      price: parseInt(variantCard.dataset.variantPrice) || 0,
      comparePrice: parseInt(variantCard.dataset.variantComparePrice) || 0,
      badgeType: variantCard.dataset.badgeType,
      badgeText: variantCard.dataset.badgeText
    };
  }

  /**
   * Update selected products array based on quantity changes
   * @param {Object} variantData - The variant data
   * @param {number} oldQuantity - Previous quantity
   * @param {number} newQuantity - New quantity
   */
  updateSelectedProductsByQuantity(variantData, oldQuantity, newQuantity) {
    const variantId = variantData.id;
    
    // Remove all instances of this variant from the array
    this.selectedProducts = this.selectedProducts.filter(product => product.id !== variantId);
    
    // Add new instances based on the new quantity
    for (let i = 0; i < newQuantity; i++) {
      const product = {
        ...variantData,
        quantity: 1, // Each instance represents quantity 1
        addedAt: new Date().toISOString(),
        instanceIndex: i // Track which instance this is
      };
      this.selectedProducts.push(product);
    }
    
    // Dispatch events for product selection change
    document.dispatchEvent(new CustomEvent('productSelection:changed', {
      detail: { selectedProducts: this.selectedProducts }
    }));
    
    // Force button state update
    if (window.customAddToCartSyncManager) {
      setTimeout(() => {
        window.customAddToCartSyncManager.handleProductSelectionChange();
      }, 100);
    }
  }

  /**
   * Sync quantities between variant card and modal
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The new quantity value
   */
  syncQuantities(variantId, quantity) {
    // Update variant card quantity - find the actual input element
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    const variantInput = variantCard ? variantCard.querySelector('.quantity-input') : null;
    const variantQuantityDiv = variantCard ? variantCard.querySelector('.product-variant-card__quantity') : null;
    const variantButton = variantCard ? variantCard.querySelector('.add-to-cart-btn') : null;
    
    if (variantInput) {
      if (variantInput.value != quantity) {
        variantInput.value = quantity;
        // Update previousValue to prevent infinite loops 
        variantInput.dataset.previousValue = quantity.toString();
      }
      
      // Always handle visibility based on quantity (not just when value changes)
      if (variantQuantityDiv && variantButton) {
        if (quantity > 0) {
          // Show quantity input, hide button
          variantButton.style.display = 'none';
          variantQuantityDiv.style.display = 'flex';
        } else {
          // Show button, hide quantity input
          variantButton.style.display = 'flex';
          variantQuantityDiv.style.display = 'none';
        }
      }
    }
    
    // Update modal quantity if it's for the same variant
    const modalInput = document.getElementById('modalQuantityInput');
    const modalButton = document.getElementById('modalAddToCartBtn');
    const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
    
    if (modalInput && this.currentVariantData && this.currentVariantData.id === variantId) {
      if (modalInput.value != quantity) {
        modalInput.value = quantity;
        // Update previousValue to prevent infinite loops
        modalInput.dataset.previousValue = quantity.toString();
      }
      
      // Handle modal visibility based on quantity
      if (modalButton && modalQuantityDiv) {
        if (quantity > 0) {
          // Show quantity input, hide button
          modalButton.style.display = 'none';
          modalQuantityDiv.style.display = 'flex';
        } else {
          // Show button, hide quantity input
          modalButton.style.display = 'flex';
          modalQuantityDiv.style.display = 'none';
        }
      }
    }
  }

  /**
   * Open product selection modal for specific buy card
   * @param {number} cardNumber - The buy card number (1-4)
   */
  openProductSelection(cardNumber) {
    this.currentCardNumber = cardNumber;
    
    // Open the variants modal
    const modal = document.getElementById('productVariantsModal');
    if (modal) {
      document.body.style.overflow = 'hidden';
      modal.showModal();
    }
  }

  /**
   * Find the first available slot and open product selection
   */
  findAndOpenAvailableSlot() {
    const quantityOptions = document.querySelectorAll('.swiper-slide:not(.duplicate-slide) .quantity-option');
    let availableSlot = null;

    for (let i = 0; i < quantityOptions.length; i++) {
      const card = quantityOptions[i];
      const hasProduct = card.querySelector('.quantity-option__product-info') || card.querySelector('.quantity-option__product-image');
      if (!hasProduct) {
        availableSlot = i + 1;
        break;
      }
    }

    if (availableSlot) {
      this.openProductSelection(availableSlot);
    } else {
      // If no available slot, use the first slot (slot 1) to allow product replacement
      this.openProductSelection(1);
    }
  }

  /**
   * Handle variant selection
   * @param {HTMLElement} variantCard - The selected variant card element
   */
  selectVariant(variantCard) {
    const descriptionElement = variantCard.querySelector('.product-variant-card__description');
    const variantData = {
      id: variantCard.dataset.variantId,
      title: variantCard.dataset.variantTitle,
      image: variantCard.dataset.variantImage,
      description: descriptionElement ? descriptionElement.textContent : 'Product description not available.',
      available: variantCard.dataset.variantAvailable === 'true',
      inventory: parseInt(variantCard.dataset.variantInventory) || 0,
      price: parseInt(variantCard.dataset.variantPrice) || 0,
      comparePrice: parseInt(variantCard.dataset.variantComparePrice) || 0,
      badgeType: variantCard.dataset.badgeType,
      badgeText: variantCard.dataset.badgeText
    };

    // Store the selected variant data
    this.currentVariantData = variantData;

    // Open product detail modal
    this.openProductDetailModal(variantData);
  }

  /**
   * Open product detail modal with variant data
   * @param {Object} variantData - The variant data object
   */
  openProductDetailModal(variantData) {
    const modal = document.getElementById('productDetailModal');
    const image = document.getElementById('productDetailImage');
    const title = document.getElementById('productDetailTitle');
    const description = document.querySelector('.product-detail-modal__description p');
    const modalQuantityInput = document.getElementById('modalQuantityInput');
    const modalButton = document.getElementById('modalAddToCartBtn');
    const modalQuantityDiv = document.querySelector('.product-detail-modal__quantity');
    
    // Populate modal with variant data
    if (image) {
      image.src = variantData.image;
      image.alt = variantData.title;
    }
    if (title) {
      title.textContent = variantData.title;
    }
    if (description && variantData.description) {
      description.textContent = variantData.description;
    }
    
    // Check variant card visibility state and sync to modal
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantData.id}"]`);
    const variantQuantityDiv = variantCard ? variantCard.querySelector('.product-variant-card__quantity') : null;
    const variantButton = variantCard ? variantCard.querySelector('.add-to-cart-btn') : null;
    const variantInput = variantQuantityDiv ? variantQuantityDiv.querySelector('.quantity-input') : null;
    
    if (modalQuantityInput && modalButton && modalQuantityDiv) {
      // Check if variant card is showing quantity input or button
      const isQuantityVisible = variantQuantityDiv && window.getComputedStyle(variantQuantityDiv).display !== 'none';
      const quantity = variantInput ? variantInput.value : '0';
      
      // Sync quantity value
      modalQuantityInput.value = quantity;
      modalQuantityInput.dataset.previousValue = quantity;
      
      // Sync visibility state
      if (isQuantityVisible) {
        // Show quantity input, hide button
        modalButton.style.display = 'none';
        modalQuantityDiv.style.display = 'flex';
      } else {
        // Show button, hide quantity input
        modalButton.style.display = 'flex';
        modalQuantityDiv.style.display = 'none';
      }
    }
    
    // Close variants modal and open detail modal
    this.closeVariantsModal();
    if (modal) {
      modal.showModal();
    }
  }



  /**
   * Update the buy cards based on quantity inputs from variant cards
   */
  updateCards() {
    const quantityOptions = document.querySelectorAll('.quantity-option');
    const variantCards = document.querySelectorAll('.product-variant-card');
    
    // Reset all original cards first
    quantityOptions.forEach((option, index) => {
      this.resetCard(option, index + 1);
    });

    // Remove any existing duplicate cards
    this.removeDuplicateCards();

    // Collect all individual quantity units from all variants
    const individualSlides = [];
    
    variantCards.forEach(variantCard => {
      const quantityInput = variantCard.querySelector('.quantity-input');
      const quantity = parseInt(quantityInput?.value) || 0;
      
      if (quantity > 0) {
        const variantData = this.getVariantDataFromCard(variantCard);
        if (variantData) {
          // Create individual slides for each quantity unit
          for (let i = 0; i < quantity; i++) {
            individualSlides.push({
              ...variantData,
              quantity: 1, // Each slide represents 1 unit
              variantId: variantData.id,
              slideIndex: i // Track which unit this is for the same variant
            });
          }
        }
      }
    });

    // Sort slides by variant ID for better organization
    individualSlides.sort((a, b) => {
      if (a.variantId === b.variantId) {
        return a.slideIndex - b.slideIndex; // Same variant, sort by slide index
      }
      return a.variantId.localeCompare(b.variantId); // Different variants, sort by ID
    });

    // Fill original cards (up to 4)
    individualSlides.forEach((slideData, index) => {
      if (index < this.maxProducts) {
        this.fillCard(quantityOptions[index], slideData, index + 1);
      }
    });

    // Create duplicate slides for overflow (each quantity unit gets its own slide)
    if (individualSlides.length > this.maxProducts) {
      this.createDuplicateSlides(individualSlides.slice(this.maxProducts));
    }

    // Update swiper after card changes
    this.updateSwiper();

    // Update savings badge based on selected products
    this.updateSavingsBadge();
    
    // Update pricing display based on selected products
   
    this.updatePricingDisplay();
    
  }

  /**
   * Update the savings badge based on selected products and their discount values
   */
  updateSavingsBadge() {
    // Find all savings badge instances (main, modal, sticky)
    const savingsBadges = [
      { badge: document.getElementById('savingsBadge'), text: document.getElementById('savingsText') },
      { badge: document.getElementById('savingsBadgeModal'), text: document.getElementById('savingsTextModal') },
      { badge: document.getElementById('savingsBadgeSticky'), text: document.getElementById('savingsTextSticky') }
    ];

    // Find the maximum discount from all selected products
    let maxDiscount = 0;
    
    this.selectedProducts.forEach((product, index) => {
      // Get the corresponding quantity option card
      const quantityOption = document.querySelectorAll('.quantity-option')[index];
      if (quantityOption) {
        // Get the swiper slide that contains this quantity option
        const swiperSlide = quantityOption.closest('.swiper-slide');
        if (swiperSlide && swiperSlide.dataset.discount) {
          const discount = parseFloat(swiperSlide.dataset.discount) || 0;
          if (discount > maxDiscount) {
            maxDiscount = discount;
          }
        }
      }
    });

    // Update all savings badge displays
    savingsBadges.forEach(({ badge, text }) => {
      if (badge && text) {
        if (maxDiscount > 0) {
          text.textContent = `YOU SAVE EXTRA $${maxDiscount.toFixed(0)}`;
          badge.style.display = 'block';
        } else {
          badge.style.display = 'none';
        }
      }
    });
  }

  /**
   * Get bundle discount based on quantity of selected products
   */
  getBundleDiscount(quantity) {
    // Find the swiper slide that corresponds to this quantity
    const quantityOptions = document.querySelectorAll('.quantity-option');
    
    // Look for the quantity option that matches our selected quantity
    for (let i = 0; i < quantityOptions.length; i++) {
      const quantityOption = quantityOptions[i];
      const swiperSlide = quantityOption.closest('.swiper-slide');
      
      if (swiperSlide) {
        // Check if this slide represents the quantity we're looking for
        // The quantity is typically represented by the index + 1
        const slideQuantity = i + 1;
        
        if (slideQuantity === quantity && swiperSlide.dataset.discount) {
          return parseFloat(swiperSlide.dataset.discount) || 0;
        }
      }
    }
    
    return 0; // No discount found
  }

  /**
   * Update the pricing display based on selected products
   */
  updatePricingDisplay() {
    // Find all pricing instances (main, modal, sticky)
    const pricingInstances = [
      {
        price: document.querySelector('.product-selection__price'),
        comparePrice: document.querySelector('.product-selection__compare-price')
      },
      {
        price: document.querySelector('.modal-add-to-cart .product-selection__price'),
        comparePrice: document.querySelector('.modal-add-to-cart .product-selection__compare-price')
      },
      {
        price: document.querySelector('.sticky-add-to-cart .product-selection__price'),
        comparePrice: document.querySelector('.sticky-add-to-cart .product-selection__compare-price')
      }
    ];

    // If no products selected, show default price
    if (this.selectedProducts.length === 0) {
      // Get the default variant price from the page
      const defaultPrice = this.getDefaultPrice();
      const defaultComparePrice = this.getDefaultComparePrice();
      
      pricingInstances.forEach(({ price, comparePrice }) => {
        if (price) {
          price.textContent = defaultPrice;
        }
        
        if (comparePrice) {
          if (defaultComparePrice) {
            comparePrice.textContent = defaultComparePrice;
            comparePrice.style.display = 'inline';
          } else {
            comparePrice.style.display = 'none';
          }
        }
      });
      return;
    }

    // Calculate total price from selected products
    let totalPrice = 0;
    let totalComparePrice = 0;
    let hasComparePrice = false;

    this.selectedProducts.forEach(product => {
      // Use the price data from variant selection (stored in cents)
      const variantPrice = product.price || 0;
      const variantComparePrice = product.comparePrice || 0;
      
      totalPrice += variantPrice;
      
      if (variantComparePrice && variantComparePrice > variantPrice) {
        totalComparePrice += variantComparePrice;
        hasComparePrice = true;
      } else {
        totalComparePrice += variantPrice;
      }
    });

    // Apply bundle discount based on quantity
    const bundleDiscount = this.getBundleDiscount(this.selectedProducts.length);
    const discountInCents = bundleDiscount * 100; // Convert to cents
    let finalPrice = Math.max(0, totalPrice - discountInCents); // Ensure price doesn't go negative
     if (document.body.classList.contains('template-flower-2')) {
      finalPrice =  Math.max(0, totalPrice);
     }
    // Format and display the final price for all instances
    const formattedPrice = this.formatPrice(finalPrice);
    const formattedComparePrice = this.formatPrice(totalComparePrice);

    pricingInstances.forEach(({ price, comparePrice }) => {
      if (price) {
        price.textContent = formattedPrice;
      }
      
      if (comparePrice) {
        if (hasComparePrice && totalComparePrice > totalPrice) {
          // Keep compare price unchanged (no discount applied)
          comparePrice.textContent = formattedComparePrice;
          comparePrice.style.display = 'inline';
        } else {
          comparePrice.style.display = 'none';
        }
      }
    });
  }

  /**
   * Get the default product price from the page
   */
  getDefaultPrice() {
    // Try to get from the pricing container data attribute
    const pricingContainer = document.querySelector('.product-selection__pricing[data-product-price]');
    if (pricingContainer && pricingContainer.dataset.productPrice) {
      return pricingContainer.dataset.productPrice;
    }
    
    // Fallback: try to parse from existing price element
    const priceElement = document.querySelector('.product-selection__price');
    if (priceElement && priceElement.textContent) {
      return priceElement.textContent.trim();
    }
    
    return '$0.00';
  }

  /**
   * Get the default product compare price from the page
   */
  getDefaultComparePrice() {
    // Try to get from the pricing container data attribute
    const pricingContainer = document.querySelector('.product-selection__pricing[data-product-compare-price]');
    if (pricingContainer && pricingContainer.dataset.productComparePrice) {
      return pricingContainer.dataset.productComparePrice;
    }
    
    // Fallback: try to parse from existing compare price element
    const comparePriceElement = document.querySelector('.product-selection__compare-price');
    if (comparePriceElement && comparePriceElement.textContent && comparePriceElement.style.display !== 'none') {
      return comparePriceElement.textContent.trim();
    }
    
    return null;
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    if (!priceInCents || priceInCents === 0) return '$0.00';
    
    const dollars = priceInCents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Reset a card to its default state
   * @param {HTMLElement} cardElement - The card element
   * @param {number} cardNumber - The card number
   */
  resetCard(cardElement, cardNumber) {
    const plusBtn = cardElement.querySelector('.quantity-option__plus-btn');
    const info = cardElement.querySelector('.quantity-option__info');
    
    // Remove any existing product image
    const existingImage = cardElement.querySelector('.quantity-option__product-image');
    if (existingImage) {
      existingImage.remove();
    }

    // Reset plus button
    if (plusBtn) {
      plusBtn.style.display = 'flex';
      plusBtn.style.opacity = '1';
    }

    // Reset card styling
    cardElement.classList.remove('quantity-option--filled');
    
    // Reset info content and make it visible
    if (info) {
      info.style.display = 'block'; // Make sure info is visible
      const savings = info.querySelector('.quantity-option__savings');
      const label = info.querySelector('.quantity-option__label');
      
      if (savings) {
        savings.style.display = savings.textContent.trim() ? 'block' : 'none';
      }
      if (label) {
        label.textContent = `Buy ${cardNumber}`;
        label.style.display = 'block';
      }
    }
  }

  /**
   * Fill a card with product data
   * @param {HTMLElement} cardElement - The card element
   * @param {Object} product - The product data
   * @param {number} cardNumber - The card number
   */
  fillCard(cardElement, product, cardNumber) {
    const plusBtn = cardElement.querySelector('.quantity-option__plus-btn');
    const info = cardElement.querySelector('.quantity-option__info');
    
    // Hide plus button and info when card is filled
    if (plusBtn) {
      plusBtn.style.display = 'none';
    }
    if (info) {
      info.style.display = 'none';
    }
    
    // Add filled class
    cardElement.classList.add('quantity-option--filled');

    // Create or update product image
    let productImage = cardElement.querySelector('.quantity-option__product-image');
    if (!productImage) {
      productImage = document.createElement('div');
      productImage.className = 'quantity-option__product-image';
      cardElement.querySelector('.quantity-option__content').appendChild(productImage);
    }

    // Generate stock label based on badge metafields
    let stockLabel = '';
    if (product.badgeType === 'Stock' && product.inventory !== undefined && product.inventory <= 10) {
      stockLabel = `${product.inventory} LEFT`;
    } else if (product.badgeType === 'Custom Text' && product.badgeText) {
      stockLabel = product.badgeText;
    }
    
    // Show quantity in the card title
    const quantityText = product.quantity > 1 ? ` (x${product.quantity})` : '';
    
    productImage.innerHTML = `
      <img src="${product.image}" alt="${product.title}" />
      <div class="quantity-option__product-title">${product.title}${quantityText}</div>
      <div class="quantity-option__product-overlay">
        <div class="quantity-option__product-actions">
        ${stockLabel ? `<div class="quantity-option__stock-label">${stockLabel}</div>` : ''}
          <button class="quantity-option__clear-btn" data-variant-id="${product.variantId}" title="Clear product">
            <svg class="icon icon-close" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Add event listener to existing clear button for individual unit removal
    const clearBtn = productImage.querySelector('.quantity-option__clear-btn');
    if (clearBtn) {
      clearBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeIndividualUnit(product.variantId);
      };
    }
  }

  /**
   * Close variants modal
   */
  closeVariantsModal() {
    const modal = document.getElementById('productVariantsModal');

    
    if (modal) {
      document.body.style.overflow = '';
      modal.close();
    }
  }

  /**
   * Close product detail modal
   */
  closeProductDetailModal() {
    const modal = document.getElementById('productDetailModal');
    if (modal) {
      document.body.style.overflow = '';
      modal.close();
    }
  }

  /**
   * Get selected products
   * @returns {Array} Array of selected products
   */
  getSelectedProducts() {
    return this.selectedProducts;
  }

  /**
   * Clear all selected products
   */
  clearSelectedProducts() {
    this.selectedProducts = [];
    this.updateCards();
    
    // Dispatch custom event for products cleared
    document.dispatchEvent(new CustomEvent('products:updated', {
      detail: { selectedProducts: this.selectedProducts }
    }));
    document.dispatchEvent(new CustomEvent('productSelection:changed', {
      detail: { selectedProducts: this.selectedProducts }
    }));
  }

  /**
   * Remove a specific product by index
   * @param {number} index - The index of the product to remove
   */
  removeProduct(index) {
    if (index >= 0 && index < this.selectedProducts.length) {
      const removedProduct = this.selectedProducts.splice(index, 1)[0];
      
      // Remove duplicate cards before updating
      this.removeDuplicateCards();
      
      this.updateCards();
      
      // Dispatch custom event for product removal
      document.dispatchEvent(new CustomEvent('product:removed', {
        detail: { product: removedProduct, selectedProducts: this.selectedProducts }
      }));
      document.dispatchEvent(new CustomEvent('productSelection:changed', {
        detail: { selectedProducts: this.selectedProducts }
      }));

      
      // Force button state update
      if (window.customAddToCartSyncManager) {
        setTimeout(() => {
          window.customAddToCartSyncManager.handleProductSelectionChange();
        }, 100);
      }
    }
  }

  /**
   * Clear a specific product by variant ID (reset quantity to 0)
   * @param {string} variantId - The variant ID to clear
   */
  clearProductByVariantId(variantId) {
    // Remove duplicate cards before clearing
    this.removeDuplicateCards();
    
    // Find the variant card with this variant ID
    const variantCards = document.querySelectorAll('.product-variant-card');
    variantCards.forEach(card => {
      const cardVariantId = card.dataset.variantId;
      if (cardVariantId === variantId) {
        const quantityInput = card.querySelector('.quantity-input');
        if (quantityInput) {
          // Set quantity to 0, which will trigger the updateQuantity logic
          quantityInput.value = '0';
          // Trigger change event to update the system
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
    
    // Also check modal input if it exists
    const modalQuantityInput = document.getElementById('modalQuantityInput');
    const modalVariantId = modalQuantityInput?.dataset.variantId;
    if (modalQuantityInput && modalVariantId === variantId) {
      modalQuantityInput.value = '0';
      modalQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Create duplicate slides for overflow individual quantity units
   * @param {Array} overflowSlides - Individual quantity units that exceed the 4 card limit
   */
  createDuplicateSlides(overflowSlides) {
    const swiperWrapper = document.querySelector('.product-selection-swiper .swiper-wrapper');
    if (!swiperWrapper) return;

    overflowSlides.forEach((slideData, index) => {
      const duplicateSlide = this.createDuplicateSlide(slideData, this.maxProducts + index + 1);
      swiperWrapper.appendChild(duplicateSlide);
    });
  }

  /**
   * Create a duplicate swiper slide by cloning the original slide structure
   * @param {Object} slideData - Individual slide data (represents 1 quantity unit)
   * @param {number} cardNumber - Card number for labeling
   * @returns {HTMLElement} - The created slide element
   */
  createDuplicateSlide(slideData, cardNumber) {
    // Find the first original slide to use as template
    const originalSlide = document.querySelector('.product-selection-swiper .swiper-slide:not(.duplicate-slide)');
    if (!originalSlide) return null;

    // Clone the original slide structure
    const slide = originalSlide.cloneNode(true);
    slide.classList.add('duplicate-slide');
    slide.dataset.discount = '0'; // Default discount for duplicates

    // Get the quantity option element
    const quantityOption = slide.querySelector('.quantity-option');
    if (quantityOption) {
      quantityOption.classList.add('quantity-option--filled');
      quantityOption.dataset.quantity = cardNumber;

      // Fill the slide with product data (same as fillCard method)
      this.fillSlide(quantityOption, slideData, cardNumber);
    }

    return slide;
  }

  /**
   * Remove all duplicate slides from the swiper
   */
  removeDuplicateCards() {
    const duplicateSlides = document.querySelectorAll('.duplicate-slide');
    duplicateSlides.forEach(slide => slide.remove());
  }

  /**
   * Fill a slide with product data (similar to fillCard but for individual slides)
   * @param {HTMLElement} quantityOption - The quantity option element
   * @param {Object} slideData - The slide data
   * @param {number} cardNumber - Card number
   */
  fillSlide(quantityOption, slideData, cardNumber) {
    const content = quantityOption.querySelector('.quantity-option__content');
    
    // Hide plus button and info when slide is filled
    const plusBtn = quantityOption.querySelector('.quantity-option__plus-btn');
    const info = quantityOption.querySelector('.quantity-option__info');
    if (plusBtn) {
      plusBtn.style.display = 'none';
    }
    if (info) {
      info.style.display = 'none';
    }

    // Create or update product image
    let productImage = quantityOption.querySelector('.quantity-option__product-image');
    if (!productImage) {
      productImage = document.createElement('div');
      productImage.className = 'quantity-option__product-image';
      content.appendChild(productImage);
    }

    // Stock label
    let stockLabel = '';
    if (slideData.badgeType === 'Stock' && slideData.inventory !== undefined && slideData.inventory <= 10) {
      stockLabel = `${slideData.inventory} LEFT`;
    } else if (slideData.badgeType === 'Custom Text' && slideData.badgeText) {
      stockLabel = slideData.badgeText;
    }

    // Update product image content with existing structure
    productImage.innerHTML = `
      <img src="${slideData.image}" alt="${slideData.title}" />
      <div class="quantity-option__product-title">${slideData.title}</div>
      <div class="quantity-option__product-overlay">
        <div class="quantity-option__product-actions">
        ${stockLabel ? `<div class="quantity-option__stock-label">${stockLabel}</div>` : ''}
          <button class="quantity-option__clear-btn" data-variant-id="${slideData.variantId}" title="Clear product">
            <svg class="icon icon-close" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Add event listener to existing clear button for individual unit removal
    const clearBtn = productImage.querySelector('.quantity-option__clear-btn');
    if (clearBtn) {
      clearBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeIndividualUnit(slideData.variantId);
      };
    }
  }



  /**
   * Remove one individual unit of a variant and recalculate
   * @param {string} variantId - The variant ID to remove one unit from
   */
  removeIndividualUnit(variantId) {
    // Find the variant card
    const variantCard = document.querySelector(`.product-variant-card[data-variant-id="${variantId}"]`);
    if (!variantCard) return;
    
    const quantityInput = variantCard.querySelector('.quantity-input');
    if (!quantityInput) return;
    
    const oldValue = parseInt(quantityInput.value) || 0;
    
    // Set quantity to 0
    quantityInput.value = '0';
    quantityInput.dataset.previousValue = '0';
    
    // Also update modal input if it's for the same variant
    const modalInput = document.getElementById('modalQuantityInput');
    if (modalInput && this.currentVariantData && this.currentVariantData.id === variantId) {
      modalInput.value = '0';
      modalInput.dataset.previousValue = '0';
    }
    
    // Get variant data and force update
    const variantData = this.getVariantDataFromCard(variantCard);
    if (variantData) {
      // Update selected products array
      this.updateSelectedProductsByQuantity(variantData, oldValue, 0);
      
      // Force sync quantities to handle visibility
      this.syncQuantities(variantId, 0);
      
      // Update cards display
      this.updateCards();
    }
  }

  /**
   * Update swiper after adding/removing slides
   */
  updateSwiper() {
    // Get the swiper instance
    const swiperElement = document.querySelector('.product-selection-swiper');
    if (swiperElement && swiperElement.swiper) {
      // Update swiper to recognize new slides
      swiperElement.swiper.update();
    }
  }


}

// Initialize the product selection manager when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit to ensure all other scripts are loaded
  setTimeout(() => {
    window.productSelectionManager = new ProductSelectionManager();
  }, 100);
});